import { useEffect, useRef, useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";

interface Props {
  open: boolean;
  initialText?: string;
  onApply: (instruction: string) => void;
  onCancel: () => void;
}

export default function RefineModal({ open, initialText = "", onApply, onCancel }: Props) {
  const [instruction, setInstruction] = useState(initialText);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Sync initialText when modal opens
  useEffect(() => {
    if (open) {
      setInstruction(initialText);
      // Focus textarea after open animation
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [open, initialText]);

  function handleApply() {
    if (!instruction.trim()) return;
    onApply(instruction.trim());
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        // No-op — modal only closes via Cancel or Apply (FR assumption)
      }}
      className="fixed inset-0 z-50"
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />

      {/* Panel */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-lg rounded-lg bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-700 flex flex-col gap-4 p-6">
          <DialogTitle className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Refine Document
          </DialogTitle>

          <textarea
            ref={textareaRef}
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            placeholder="Describe how you'd like to improve the document."
            rows={4}
            className="w-full resize-y rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={!instruction.trim()}
              className="px-4 py-2 text-sm rounded bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
            >
              Apply
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
